/**
 * @desc Central map: useUserSettings `feature` string → default options (panels + map).
 *       **Import this module directly** for `DASHBOARD_PANEL_SCHEMA_BY_FEATURE`, `getDefault`, etc.
 *       `dexie-db.js` only imports `DEFAULT_PAGE_SETTINGS` for IndexedDB fallbacks — do not re-export schema from there.
 * @version 1.0.10 (The chemical register has no Default View settings: its tabs are grouped and always open on Inventory) - 1 October 2026
 *          1.0.9 (The chemical register's Sessions tab message) - 24 September 2026
 **/

export const DASHBOARD_PANEL_SCHEMA_BY_FEATURE = {
    dashboard: {
        panels: [
            {
                group: 'general',
                label: 'Farm snapshot',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Feature tiles',
                order: 120,
                show: true,
            },
            {
                group: 'Demo',
                label: 'Show Demo Features',
                order: 210,
                show: true,
            },
        ],
    },
    'video-dashboard': {
        panels: [
            {
                group: 'general',
                label: 'Update Message',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Camera Video Streams',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Video Streams Table',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Map',
                order: 140,
                show: true,
            },
            {
                group: 'tabs',
                label: 'Cameras',
                order: 210,
                show: true,
            },
            {
                group: 'tabs',
                label: 'Video Wall',
                order: 220,
                show: true,
            },
        ],
        map: {},
    },
    'energy-dashboard': {
        panels: [
            {
                group: 'general',
                label: 'Update Message',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Header Summary',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Distribution Boards',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Table',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'Map',
                order: 150,
                show: true,
            },
        ],
        map: {},
    },
    'weather-dashboard': {
        panels: [
            {
                group: 'Default View',
                label: 'Set weekly as default',
                order: 105,
                show: false,
            },
            {
                group: 'Default View',
                label: 'Set on-site WS as default',
                order: 106,
                show: false,
            },
            {
                group: 'general',
                label: 'Update Message',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Local Weather',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'On-site Weather Station',
                order: 130,
                show: true,
            },
            {
                group: 'Local Weather',
                label: 'Hourly Data',
                order: 210,
                show: true,
            },
            {
                group: 'Local Weather',
                label: 'Daily Data',
                order: 220,
                show: true,
            },
            {
                group: 'Local Weather',
                label: 'Info Cells',
                order: 260,
                show: true,
            },
            {
                group: 'Local Weather',
                label: 'Rainfall',
                order: 230,
                show: true,
            },
            {
                group: 'Local Weather',
                label: 'Pressure Humidity',
                order: 234,
                show: true,
            },
            {
                group: 'Local Weather',
                label: 'Wind',
                order: 240,
                show: true,
            },
            {
                group: 'Local Weather',
                label: 'UV Index',
                order: 250,
                show: true,
            },
            {
                group: 'Weather Station',
                label: 'Todays Data',
                order: 310,
                show: true,
                disabled: true,
            },
            {
                group: 'Weather Station',
                label: 'Information Cells',
                order: 320,
                show: true,
            },
            {
                group: 'Weather Station',
                label: 'Temp/Hum/Pres Chart',
                order: 330,
                show: true,
            },
            {
                group: 'Weather Station',
                label: 'Rainfall Chart',
                order: 340,
                show: true,
            },
            {
                group: 'Weather Station',
                label: 'Wind Chart',
                order: 350,
                show: true,
            },
            {
                group: 'Weather Station',
                label: 'Rainfall History',
                order: 360,
                show: true,
            },
        ],
        map: {},
    },
    'water-dashboard': {
        panels: [
            {
                group: 'general',
                label: 'Update Message',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Header Summary',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Cards',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Table',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'Map',
                order: 150,
                show: true,
            },
            {
                group: 'farm assets',
                label: 'Include all',
                order: 210,
                show: true,
            },
        ],
        map: {},
    },
    'ble-counting-dashboard': {
        panels: [
            {
                group: 'general',
                label: 'Update Message',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Header Summary',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Gateway Summary',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Gateway Analytics',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'BLE Tag Analytics',
                order: 150,
                show: true,
            },
        ],
        map: {},
    },
    'gps-counting-dashboard': {
        panels: [
            {
                group: 'general',
                label: 'Update Message',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Header Summary',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Gateway Summary',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Gateway Analytics',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'BLE Tag Analytics',
                order: 150,
                show: true,
            },
        ],
        map: {},
    },
    farmmap: {
        panels: [
            {
                group: 'general',
                label: 'Update Message',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Header Summary',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Map',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Table',
                order: 140,
                show: true,
            },
            {
                group: 'farm assets',
                label: 'Include all',
                order: 210,
                show: true,
            },
        ],
        map: {},
    },
    'air-quality-dashboard': {
        panels: [
            {
                group: 'general',
                label: 'Update Message',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Header Summary',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Cards',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Table',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'Map',
                order: 150,
                show: true,
            },
        ],
        map: {},
    },
    'cold-storage-dashboard': {
        panels: [
            {
                group: 'general',
                label: 'Update Message',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Header Summary',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Cards',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Table',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'Map',
                order: 150,
                show: true,
            },
        ],
        map: {},
    },
    'chemicals-record': {
        panels: [
            {
                group: 'general',
                label: 'Update Message',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Chemical Products',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Equipment Setup',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Agrichemicals',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'Pest Bait Records',
                order: 145,
                show: true,
            },
            {
                group: 'general',
                label: 'Map',
                order: 150,
                show: true,
            },
        ],
        map: {},
    },
    'chemical-register-dashboard': {
        panels: [
            {
                group: 'Update Messages',
                label: 'Inventory update message',
                order: 110,
                show: true,
            },
            {
                group: 'Update Messages',
                label: 'Storage update message',
                order: 111,
                show: true,
            },
            {
                group: 'Update Messages',
                label: 'Spraying update message',
                order: 112,
                show: true,
            },
            {
                group: 'Update Messages',
                label: 'Quarantine update message',
                order: 113,
                show: true,
            },
            {
                group: 'Update Messages',
                label: 'Retired products update message',
                order: 114,
                show: true,
            },
            {
                group: 'Update Messages',
                label: 'Withholding update message',
                order: 115,
                show: true,
            },
            {
                group: 'Update Messages',
                label: 'Traceability update message',
                order: 116,
                show: true,
            },
            {
                group: 'Update Messages',
                label: 'Sessions update message',
                order: 117,
                show: true,
            },
            {
                group: 'Inventory',
                label: 'Quick actions',
                order: 120,
                show: true,
            },
            {
                group: 'Inventory',
                label: 'Inventory levels',
                order: 121,
                show: true,
            },
            {
                group: 'Inventory',
                label: 'Expiry notifications',
                order: 122,
                show: true,
            },
            {
                group: 'Inventory',
                label: 'Chemical products table',
                order: 123,
                show: true,
            },
            {
                group: 'Inventory',
                label: 'Storage map',
                order: 124,
                show: true,
            },
            {
                group: 'Storage',
                label: 'Header summary',
                order: 130,
                show: true,
            },
            {
                group: 'Storage',
                label: 'Chemical stores table',
                order: 131,
                show: true,
            },
            {
                group: 'Storage',
                label: 'Map',
                order: 132,
                show: true,
            },
            {
                group: 'Spraying',
                label: 'Header summary',
                order: 140,
                show: true,
            },
            {
                group: 'Spraying',
                label: 'Spraying equipment table',
                order: 141,
                show: true,
            },
            {
                group: 'Spraying',
                label: 'Map',
                order: 142,
                show: true,
            },
            {
                group: 'Quarantine Sites',
                label: 'Header summary',
                order: 150,
                show: true,
            },
            {
                group: 'Quarantine Sites',
                label: 'Quarantine sites table',
                order: 151,
                show: true,
            },
            {
                group: 'Quarantine Sites',
                label: 'Map',
                order: 152,
                show: true,
            },
        ],
        map: {},
    },
    'chemicalProduct-asset': {
        panels: [
            {
                group: 'general',
                label: 'Product details',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Inventory levels',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Expiry notifications',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Current inventory',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'Usage events',
                order: 150,
                show: true,
            },
            {
                group: 'general',
                label: 'Inventory events',
                order: 160,
                show: true,
            },
        ],
        map: {},
    },
    'chemicalShed-asset': {
        panels: [
            {
                group: 'general',
                label: 'Header summary',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Store details',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Inventory held here',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Inventory events',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'Map',
                order: 150,
                show: true,
            },
        ],
        map: {},
    },
    'sprayer-asset': {
        panels: [
            {
                group: 'general',
                label: 'Equipment details',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Spray events',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Map',
                order: 130,
                show: true,
            },
        ],
        map: {},
    },
    'quarantineSite-asset': {
        panels: [
            {
                group: 'general',
                label: 'Header summary',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Site details',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Inventory quarantined here',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Quarantine history',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'Map',
                order: 150,
                show: true,
            },
        ],
        map: {},
    },
    'alerts-notifications-dashboard': {
        panels: [
            {
                group: 'general',
                label: 'Update Message',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Header Summary',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Custom Alerts',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Alerting Information',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'Notifications',
                order: 150,
                show: true,
            },
        ],
        map: {},
    },
    'fencing-dashboard': {
        panels: [
            {
                group: 'general',
                label: 'Header Summary',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Fencing Table',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Fencing Map',
                order: 130,
                show: true,
            },
        ],
        map: {},
    },
    'paddocks-dashboard': {
        panels: [
            {
                group: 'general',
                label: 'Header Summary',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Cards',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Table',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'Map',
                order: 150,
                show: true,
            },
        ],
        map: {},
    },
    'animal-management-dashboard': {
        panels: [
            {
                group: 'Banners',
                label: 'Update Message',
                order: 100,
                show: true,
            },
            {
                group: 'Banners',
                label: 'No Groups Banner',
                order: 105,
                show: true,
            },
            {
                group: 'general',
                label: 'Header Summary',
                order: 110,
                show: true,
            },
            {
                group: 'general',
                label: 'Group Cards',
                order: 120,
                show: true,
            },
            {
                group: 'general',
                label: 'Groups Table',
                order: 130,
                show: true,
            },
            {
                group: 'general',
                label: 'Animals Table',
                order: 140,
                show: true,
            },
            {
                group: 'general',
                label: 'Map',
                order: 150,
                show: true,
            },
        ],
    },
}

/** Kept name for Dexie `useUserSettings` default fallback object */
export const DEFAULT_PAGE_SETTINGS = DASHBOARD_PANEL_SCHEMA_BY_FEATURE

/** Stable list of feature keys present in the schema */
export const DASHBOARD_PANEL_SCHEMA_FEATURE_KEYS = Object.freeze(Object.keys(DASHBOARD_PANEL_SCHEMA_BY_FEATURE))

/** @returns {{ panels: object[], map?: object } | undefined} */
export function getDashboardPanelSchemaForFeature(feature) {
    return DASHBOARD_PANEL_SCHEMA_BY_FEATURE[feature]
}

/** @returns {{ panels: object[], map?: object } | undefined} */
export function getDefault(feature) {
    return getDashboardPanelSchemaForFeature(feature)
}
